// The wire format of Brainyard Share (RFC-043), as the browser speaks it.
//
// This is a mirror of share/contract/protocol (frame.go, body.go): every
// frame type, every body, the share event kinds, MaxResultBytes and the
// Senders table, with the JSON keys exactly as Go writes them. `decode`
// applies the checks Go's Decode applies, in the same order: this version, a
// known type, a sender allowed to originate it, a body that fits the type
// (unknown body fields refused, at every depth), then the per-body checks.
// The golden frames under share/testdata/frames are the contract between
// the two; src/protocol.test.ts reads them straight from there.
//
// Go's encoding/json shapes the wire form, so decode normalises the way a Go
// round trip would: an absent non-omitempty field gets its zero value (an
// absent slice is null), an omitempty field at its zero value is dropped.
// One leniency of Go's is not mirrored: Go matches JSON keys to struct
// fields case-insensitively ("Text" fills `text`); here a miscased key is an
// unknown field and refused. Nothing sends one.

export const Version = 1;

// ---- frame types -----------------------------------------------------------

export type FrameType =
  | "hello"
  | "welcome"
  | "share.open"
  | "share.close"
  | "event"
  | "status"
  | "alert"
  | "note"
  | "inject.request"
  | "inject.result"
  | "presence"
  | "control"
  | "agent.request"
  | "agent.stream"
  | "agent.result"
  | "ping"
  | "pong"
  | "ack"
  | "error";

/** Every frame type, in the order RFC-043 lists them (Go `Types`). */
export const Types: readonly FrameType[] = [
  "hello",
  "welcome",
  "share.open",
  "share.close",
  "event",
  "status",
  "alert",
  "note",
  "inject.request",
  "inject.result",
  "presence",
  "control",
  "agent.request",
  "agent.stream",
  "agent.result",
  "ping",
  "pong",
  "ack",
  "error",
];

/** What sent a frame, or did what an event records (Go `Kind`). */
export type Kind = "sidecar" | "relay" | "agent" | "owner" | "coach" | "spectator" | "reviewer" | "caller";

/** A frame's sender, or an event's doer. `user` is set for people only. */
export interface Actor {
  kind: Kind;
  user?: string;
  /** How they are shown; the relay stamps it beside the id it vouches for. */
  name?: string;
}

const anyone: readonly Kind[] = ["sidecar", "relay", "owner", "coach", "spectator", "reviewer", "caller"];

/** Who may originate each frame type (Go `Senders`). */
export const Senders: Readonly<Record<FrameType, readonly Kind[]>> = {
  hello: ["sidecar"],
  welcome: ["relay"],
  "share.open": ["sidecar", "relay"],
  "share.close": ["sidecar", "relay"],
  event: ["sidecar"],
  status: ["sidecar"],
  alert: ["sidecar"],
  note: ["owner", "coach", "spectator"], // a spectator's only where the policy lets them chat (RFC-043 model 21)
  "inject.request": ["coach"],
  "inject.result": ["sidecar"],
  presence: ["relay"],
  control: ["owner"],
  "agent.request": ["caller", "owner", "coach"],
  "agent.stream": ["sidecar"],
  "agent.result": ["sidecar"],
  ping: anyone,
  pong: anyone,
  ack: anyone,
  error: ["sidecar", "relay"],
};

/** Whether a sender of kind k may originate a frame of type t (Go `MaySend`). */
export function maySend(k: string, t: string): boolean {
  const s = (Senders as Record<string, readonly string[] | undefined>)[t];
  return s !== undefined && s.includes(k);
}

// ---- bodies ----------------------------------------------------------------

export interface HelloBody {
  device_token: string;
  sidecar_version: string;
  jolt_version?: string;
  socket_frame_version?: number;
  versions: number[] | null;
}

export interface WelcomeBody {
  version: number;
  user: string;
}

/** The kind of session a share comes from, named for its CLI (RFC-044; Go `Source`). */
export type Source = "byj" | "claude" | "codex" | "agent" | "term";

/** RFC-043's names for the kinds, read from an older sidecar or relay and never sent (byj-1wv.8.1). */
const LegacySources: Record<string, Source> = { jolt: "byj", "claude-code": "claude" };

/** A source under its RFC-044 name (Go `Source.Canonical`). */
export function canonicalSource(s: string): Source {
  return LegacySources[s] ?? (s as Source);
}

export interface Target {
  kind: "link" | "class";
  class?: string;
  /** The class assignment the share works on (RFC-043 model 19); omitted when none. */
  assignment?: string;
}

/** What a student types to name an assignment (Go `AssignmentKey`). */
export const AssignmentKey = /^[a-z0-9][a-z0-9-]{0,31}$/;

/** The owner's policy for one share. The sidecar enforces it; the relay shows it. */
export interface Policy {
  notes: boolean;
  /** Coaches may request injected turns (never for claude). */
  inject: boolean;
  /** Class consent: an allowed injection needs no prompt, still marked. */
  consent: boolean;
  paused: boolean;
  /** The share's events are kept as a recording, uploaded for review when it ends. */
  record?: boolean;
  /** Spectators may send notes too, so everyone invited chats (RFC-043 model 21); absent is off. */
  chat?: boolean;
  /** An agent share on its owner's subscription, which answers its owner alone (RFC-060 D15): no caller is invited. */
  owner_only?: boolean;
  /** An agent share's owner keeps what callers ask it and its answers, and callers are told (byj-1wv.48); absent is off. */
  keep_questions?: boolean;
}

/** What a shared session can carry (RFC-044; Go `Caps`): decide from these, not the source. */
export interface Caps {
  inject?: boolean;
  notes?: boolean;
  screen?: boolean;
  cost?: boolean;
  /** The share is an agent callers ask over A2A (RFC-043 model 20). */
  agent?: boolean;
  /** The screen is the whole of the share, which sends no events but notes (RFC-043 model 21). */
  terminal?: boolean;
  /** Its owner may type into it from the page, on the screen socket. */
  input?: boolean;
  /** A Share machine's terminal whose files and agent whoever may type uses from the page, on the workbench socket (RFC-057). */
  workbench?: boolean;
}

/** The shared agent as its card says it (Go `AgentInfo`, RFC-043 model 20). */
export interface AgentInfo {
  name: string;
  description?: string;
  skills: AgentSkill[];
  /** byj's reach level for the agent, the pilot's measure (byj-1wv.49). */
  reach?: string;
}

export interface AgentSkill {
  id: string;
  name?: string;
  description?: string;
}

/** Which session a share is (Go `SessionInfo`). */
export interface SessionInfo {
  id: string;
  label?: string;
}

export interface ShareOpenBody {
  source: Source;
  title: string;
  target: Target;
  policy: Policy;
  /** Absent from a sidecar older than RFC-044: see `capsOf`. */
  caps?: Caps;
  session?: SessionInfo;
  group?: string;
  /** An agent share's card (RFC-043 model 20). */
  agent?: AgentInfo;
}

/**
 * What a share's session can carry: its `caps`, or — from a sidecar older
 * than RFC-044, which sent none, or a relay that no longer keeps them once
 * the share has ended — what its kind can. A terminal and an agent have
 * always said they are one, so an ended one's page is still its kind's.
 */
export function capsOf(source: string, caps?: Caps): Caps {
  if (caps) return caps;
  switch (canonicalSource(source)) {
    case "byj":
      return { inject: true, notes: true, cost: true };
    case "term":
      return { screen: true, terminal: true };
    case "agent":
      return { agent: true, cost: true };
    default:
      return { cost: true };
  }
}

export interface ShareCloseBody {
  /** "owner", "session-ended", "error" — not checked by Go. */
  reason: string;
}

/** The share event kinds (RFC-043 "Share event model"; Go `EventKinds`). */
export const EventKinds = [
  "turn.begin",
  "thought",
  "tool.call",
  "tool.result",
  "permission.pending",
  "permission.resolved",
  "answer",
  "turn.end",
  "note",
  "inject",
  "commit",
] as const;
export type EventKind = (typeof EventKinds)[number];

/** A tool.result's text is capped at this many UTF-8 bytes, past it cut and `truncated`. */
export const MaxResultBytes = 8 * 1024;

/** What the sidecar hid: a kind and how many, never the value. */
export interface Masked {
  kind: string;
  count: number;
}

export interface Usage {
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

/** One share event. Which fields are set depends on `kind`. */
export interface EventBody {
  kind: EventKind;
  turn: number;
  iter?: number;
  call_id?: string;
  tool?: string;
  ok?: boolean;
  text?: string;
  truncated?: boolean;
  masked?: Masked[];
  usage?: Usage;
  /** turn.end: why it ended; permission.resolved: the decision. */
  reason?: string;
  commit?: string;
  by: Actor;
  /** The subagent whose turn this is from, inside the session's (RFC-044); absent for the session's own. */
  subagent?: Subagent;
  /** The workbench agent a turn is put to (RFC-057 D5: byj, claude, codex), on its turn.begin. */
  agent?: string;
}

/** A subagent instance (Go `Subagent`): `explore/azure-whale-1`, and its agent's name. */
export interface Subagent {
  id: string;
  name?: string;
}

export type StatusState = "working" | "needs-input" | "idle" | "ended";

export interface StatusBody {
  /** A StatusState; Go does not check it. */
  state: string;
  turns: number;
  cost_usd: number;
  pending_permissions: number;
}

export interface AlertBody {
  /** "repeated-call", "no-progress", "permission-wait", "turn-failed", "cost-spike", "idle". */
  kind: string;
  seqs: number[] | null;
  detail?: string;
}

/** For people only: it never reaches the agent. */
export interface NoteBody {
  text: string;
  /** The seq it is about. */
  ref?: number;
}

export interface InjectRequestBody {
  id: string;
  text: string;
}

export const InjectOutcomes = ["accepted", "rejected", "expired", "denied"] as const;
export type InjectOutcome = (typeof InjectOutcomes)[number];

export interface InjectResultBody {
  id: string;
  outcome: InjectOutcome;
  reason?: string;
}

/** One A2A JSON-RPC request for a shared agent, carried whole (Go `AgentRequestBody`, RFC-043 model 20). */
export interface AgentRequestBody {
  id: string;
  request: unknown;
}

/** One SSE frame of a message/stream: the JSON-RPC response envelope the agent sent. */
export interface AgentStreamBody {
  id: string;
  data: unknown;
}

/** The end of an agent request: the agent's JSON-RPC response, or the sidecar's refusal. */
export interface AgentResultBody {
  id: string;
  response: unknown;
}

export interface Participant {
  user: string;
  name: string;
  role: Kind;
}

export interface PresenceBody {
  participants: Participant[] | null;
  /** The coach holding a terminal share's keyboard besides the owner (RFC-043 model 21); absent when none. */
  keyboard?: string;
}

/** `keyboard` hands a terminal share's keyboard to the coach `user`, or takes it back with none (RFC-043 model 21). */
export type ControlAction = "kick" | "pause" | "resume" | "policy" | "stop" | "keyboard";

export interface ControlBody {
  action: ControlAction;
  user?: string;
  policy?: Policy;
}

export interface AckBody {
  seq: number;
}

export interface ErrorBody {
  /** "version", "forbidden", "policy", "bad-frame", "internal". */
  code: string;
  message: string;
  ref?: string;
}

export interface BodyOf {
  hello: HelloBody;
  welcome: WelcomeBody;
  "share.open": ShareOpenBody;
  "share.close": ShareCloseBody;
  event: EventBody;
  status: StatusBody;
  alert: AlertBody;
  note: NoteBody;
  "inject.request": InjectRequestBody;
  "inject.result": InjectResultBody;
  presence: PresenceBody;
  control: ControlBody;
  "agent.request": AgentRequestBody;
  "agent.stream": AgentStreamBody;
  "agent.result": AgentResultBody;
  ping: null;
  pong: null;
  ack: AckBody;
  error: ErrorBody;
}

interface EnvelopeOf<T extends FrameType> {
  v: number;
  type: T;
  share?: string;
  /** The sidecar's, monotonic within a share. */
  seq?: number;
  /** RFC 3339, as Go's time.Time writes it. */
  ts: string;
  from: Actor;
  // ping and pong carry no body (Go tolerates null or {}).
  body: BodyOf[T] extends null ? null | Record<string, never> | undefined : BodyOf[T];
}

/** One frame: {v, type, share, seq, ts, from, body}, discriminated by `type`. */
export type Envelope = { [T in FrameType]: EnvelopeOf<T> }[FrameType];
export type EnvelopeFor<T extends FrameType> = Extract<Envelope, { type: T }>;

// ---- decoding: a small mirror of encoding/json ------------------------------

export class ProtocolError extends Error {
  constructor(
    message: string,
    readonly code: "version" | "bad-frame" = "bad-frame",
  ) {
    super(`protocol: ${message}`);
    this.name = "ProtocolError";
  }
}

type Spec =
  | { t: "string" }
  | { t: "int" }
  | { t: "uint" }
  | { t: "float" }
  | { t: "bool" }
  | { t: "time" }
  | { t: "ptr"; of: Spec }
  | { t: "slice"; of: Spec }
  | { t: "struct"; fields: Record<string, Field> }
  | { t: "any" }; // a json.RawMessage: carried as it came

interface Field {
  spec: Spec;
  omitempty?: boolean;
}

const str: Spec = { t: "string" };
const int: Spec = { t: "int" };
const uint: Spec = { t: "uint" };
const float: Spec = { t: "float" };
const bool: Spec = { t: "bool" };
const any: Spec = { t: "any" };
const f = (spec: Spec, omitempty = false): Field => ({ spec, omitempty });
const struct = (fields: Record<string, Field>): Spec => ({ t: "struct", fields });

const actorSpec = struct({ kind: f(str), user: f(str, true), name: f(str, true) });
const policySpec = struct({ notes: f(bool), inject: f(bool), consent: f(bool), paused: f(bool), record: f(bool, true), chat: f(bool, true), keep_questions: f(bool, true) });

const bodySpecs: Record<Exclude<FrameType, "ping" | "pong">, Spec> = {
  hello: struct({
    device_token: f(str),
    sidecar_version: f(str),
    jolt_version: f(str, true),
    socket_frame_version: f(int, true),
    versions: f({ t: "slice", of: int }),
  }),
  welcome: struct({ version: f(int), user: f(str) }),
  "share.open": struct({
    source: f(str),
    title: f(str),
    target: f(struct({ kind: f(str), class: f(str, true), assignment: f(str, true) })),
    policy: f(policySpec),
    caps: f({ t: "ptr", of: struct({ inject: f(bool, true), notes: f(bool, true), screen: f(bool, true), cost: f(bool, true), agent: f(bool, true), terminal: f(bool, true), input: f(bool, true), workbench: f(bool, true) }) }, true),
    session: f({ t: "ptr", of: struct({ id: f(str), label: f(str, true) }) }, true),
    agent: f(
      {
        t: "ptr",
        of: struct({
          name: f(str),
          description: f(str, true),
          skills: f({ t: "slice", of: struct({ id: f(str), name: f(str, true), description: f(str, true) }) }),
          reach: f(str, true),
        }),
      },
      true,
    ),
  }),
  "share.close": struct({ reason: f(str) }),
  event: struct({
    kind: f(str),
    turn: f(int),
    iter: f(int, true),
    call_id: f(str, true),
    tool: f(str, true),
    ok: f({ t: "ptr", of: bool }, true),
    text: f(str, true),
    truncated: f(bool, true),
    masked: f({ t: "slice", of: struct({ kind: f(str), count: f(int) }) }, true),
    usage: f(
      { t: "ptr", of: struct({ input_tokens: f(int), output_tokens: f(int), cost_usd: f(float) }) },
      true,
    ),
    reason: f(str, true),
    commit: f(str, true),
    by: f(actorSpec),
    subagent: f({ t: "ptr", of: struct({ id: f(str), name: f(str, true) }) }, true),
    agent: f(str, true),
  }),
  status: struct({ state: f(str), turns: f(int), cost_usd: f(float), pending_permissions: f(int) }),
  alert: struct({ kind: f(str), seqs: f({ t: "slice", of: uint }), detail: f(str, true) }),
  note: struct({ text: f(str), ref: f(uint, true) }),
  "inject.request": struct({ id: f(str), text: f(str) }),
  "inject.result": struct({ id: f(str), outcome: f(str), reason: f(str, true) }),
  "agent.request": struct({ id: f(str), request: f(any) }),
  "agent.stream": struct({ id: f(str), data: f(any) }),
  "agent.result": struct({ id: f(str), response: f(any) }),
  presence: struct({
    participants: f({ t: "slice", of: struct({ user: f(str), name: f(str), role: f(str) }) }),
    keyboard: f(str, true),
  }),
  control: struct({ action: f(str), user: f(str, true), policy: f({ t: "ptr", of: policySpec }, true) }),
  ack: struct({ seq: f(uint) }),
  error: struct({ code: f(str), message: f(str), ref: f(str, true) }),
};

const RFC3339 = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?(Z|[+-](\d{2}):(\d{2}))$/;

/** A time as Go's time.Time.MarshalJSON writes it back: RFC3339Nano. */
function goTime(s: string, where: string): string {
  const m = RFC3339.exec(s);
  if (!m) throw new ProtocolError(`${where}: cannot parse ${JSON.stringify(s)} as RFC 3339`);
  const [, , mo, d, h, mi, sec, frac, zone, zh, zm] = m;
  const bad =
    Number(mo) < 1 ||
    Number(mo) > 12 ||
    Number(d) < 1 ||
    Number(d) > 31 ||
    Number(h) > 23 ||
    Number(mi) > 59 ||
    Number(sec) > 59 ||
    (zh !== undefined && Number(zh) > 23) ||
    (zm !== undefined && Number(zm) > 59);
  if (bad) throw new ProtocolError(`${where}: time ${JSON.stringify(s)} out of range`);
  // RFC3339Nano trims trailing zeros from the fraction, and an all-zero one.
  let f2 = (frac ?? "").replace(/0+$/, "");
  if (f2 === ".") f2 = "";
  return s.slice(0, 19) + f2 + zone;
}

const zeroTime = "0001-01-01T00:00:00Z";

function zero(spec: Spec): unknown {
  switch (spec.t) {
    case "any":
      return undefined;
    case "string":
      return "";
    case "int":
    case "uint":
    case "float":
      return 0;
    case "bool":
      return false;
    case "time":
      return zeroTime;
    case "ptr":
      return undefined;
    case "slice":
      return null;
    case "struct":
      return shapeStruct(spec, {}, "", false);
  }
}

function isEmpty(spec: Spec, v: unknown): boolean {
  switch (spec.t) {
    case "slice":
      return v === null || (Array.isArray(v) && v.length === 0);
    case "ptr":
      return v === undefined;
    case "struct":
    case "time":
    case "any":
      return false; // omitempty never omits a struct
    default:
      return v === "" || v === 0 || v === false;
  }
}

const typeName = (v: unknown) => (v === null ? "null" : Array.isArray(v) ? "array" : typeof v);

/** Shape one JSON value by spec, the way json.Unmarshal into the Go type would. */
function shape(spec: Spec, v: unknown, where: string, strict: boolean): unknown {
  if (v === null) {
    // null into a non-pointer, non-slice Go value has no effect: its zero.
    return spec.t === "slice" || spec.t === "any" ? null : zero(spec);
  }
  const mismatch = () =>
    new ProtocolError(`${where}: cannot unmarshal ${typeName(v)} into a Go ${spec.t}`);
  switch (spec.t) {
    case "any":
      return v;
    case "string":
      if (typeof v !== "string") throw mismatch();
      return v;
    case "bool":
      if (typeof v !== "boolean") throw mismatch();
      return v;
    case "float":
      if (typeof v !== "number") throw mismatch();
      return v;
    case "int":
      if (typeof v !== "number" || !Number.isInteger(v)) throw mismatch();
      return v;
    case "uint":
      if (typeof v !== "number" || !Number.isInteger(v) || v < 0) throw mismatch();
      return v;
    case "time":
      if (typeof v !== "string") throw mismatch();
      return goTime(v, where);
    case "ptr":
      return shape(spec.of, v, where, strict);
    case "slice":
      if (!Array.isArray(v)) throw mismatch();
      return v.map((x, i) => shape(spec.of, x, `${where}[${i}]`, strict));
    case "struct":
      if (typeof v !== "object" || Array.isArray(v)) throw mismatch();
      return shapeStruct(spec, v as Record<string, unknown>, where, strict);
  }
}

function shapeStruct(
  spec: Extract<Spec, { t: "struct" }>,
  v: Record<string, unknown>,
  where: string,
  strict: boolean,
): Record<string, unknown> {
  if (strict) {
    for (const k of Object.keys(v)) {
      if (!Object.hasOwn(spec.fields, k)) {
        throw new ProtocolError(`${where}: json: unknown field ${JSON.stringify(k)}`);
      }
    }
  }
  const out: Record<string, unknown> = {};
  for (const [k, field] of Object.entries(spec.fields)) {
    const val = Object.hasOwn(v, k)
      ? shape(field.spec, v[k], where ? `${where}.${k}` : k, strict)
      : zero(field.spec);
    if (val === undefined) continue;
    if (field.omitempty && isEmpty(field.spec, val)) continue;
    out[k] = val;
  }
  return out;
}

const envelopeSpec = struct({
  v: f(int),
  type: f(str),
  share: f(str, true),
  seq: f(uint, true),
  ts: f({ t: "time" }),
  from: f(actorSpec),
});

function utf8Length(s: string): number {
  return new TextEncoder().encode(s).length;
}

function checkBody(type: FrameType, b: Record<string, unknown>): void {
  const fail = (m: string) => {
    throw new ProtocolError(`${type} body: ${m}`);
  };
  switch (type) {
    case "hello":
      if (b.device_token === "") fail("device_token is required");
      if (!Array.isArray(b.versions) || b.versions.length === 0) fail("versions is required");
      return;
    case "share.open": {
      const t = b.target as Target;
      const p = b.policy as Policy;
      b.source = canonicalSource(b.source as string);
      if (b.source !== "byj" && b.source !== "claude" && b.source !== "codex" && b.source !== "agent" && b.source !== "term") fail(`unknown source ${JSON.stringify(b.source)}`);
      if (t.kind !== "link" && t.kind !== "class") fail(`unknown target ${JSON.stringify(t.kind)}`);
      if (t.kind === "class" && !t.class) fail("a class target names its class");
      if (t.assignment !== undefined) {
        if (t.kind !== "class") fail("an assignment needs a class target");
        if (!AssignmentKey.test(t.assignment)) fail(`an assignment key is lowercase letters, digits and dashes, 1-32 long, not ${JSON.stringify(t.assignment)}`);
      }
      if (b.source === "claude" && p.inject) fail("a claude share cannot take injected turns");
      const caps = b.caps as Caps | undefined;
      if (caps && !caps.inject && p.inject) fail("a share whose session takes no injected turns cannot allow them");
      // a terminal share says so in its caps, and only a terminal takes the owner's typing (RFC-043 model 21)
      if (b.source === "term" && !caps?.terminal) fail("a terminal share carries caps saying it is a terminal");
      if (caps?.input && !caps.terminal) fail("only a terminal share takes input");
      // a machine's terminal alone has a workbench, used by whoever may type (RFC-057)
      if (caps?.workbench && !caps.input) fail("only a machine's terminal that takes input has a workbench");
      const agent = b.agent as AgentInfo | undefined;
      if (b.source === "agent") {
        if (!agent || !agent.name || !agent.skills || agent.skills.length === 0) fail("an agent share carries its agent's card: a name and its skills");
        for (const sk of agent!.skills) if (!sk.id) fail("an agent's skill has an id");
      } else if (agent !== undefined) {
        fail("only an agent share carries an agent");
      }
      return;
    }
    case "event": {
      const by = b.by as Actor;
      if (!(EventKinds as readonly string[]).includes(b.kind as string)) {
        fail(`unknown event kind ${JSON.stringify(b.kind)}`);
      }
      if (b.kind === "tool.result" && typeof b.text === "string" && utf8Length(b.text) > MaxResultBytes) {
        fail(`tool.result text over ${MaxResultBytes} bytes`);
      }
      if (by.kind === "spectator") {
        // a note, where the policy lets spectators chat (RFC-043 model 21)
        if (b.kind !== "note") fail(`a spectator's event is a note, not ${JSON.stringify(b.kind)}`);
      } else if (by.kind !== "agent" && by.kind !== "owner" && by.kind !== "coach" && by.kind !== "caller") {
        fail(`an event is by agent, owner, coach or caller, not ${JSON.stringify(by.kind)}`);
      }
      if ((by.kind === "coach" || by.kind === "caller" || by.kind === "spectator") && !by.user) fail(`a ${by.kind}'s event names the ${by.kind}`);
      if (b.subagent && !(b.subagent as Subagent).id) fail("a subagent's event names its subagent");
      return;
    }
    case "note":
      if (b.text === "") fail("empty note");
      return;
    case "inject.request":
      if (b.id === "" || b.text === "") fail("an injection has an id and a text");
      return;
    case "inject.result":
      if (!(InjectOutcomes as readonly string[]).includes(b.outcome as string)) {
        fail(`unknown outcome ${JSON.stringify(b.outcome)}`);
      }
      return;
    case "agent.request": {
      if (b.id === "") fail("an agent request has an id");
      const req = b.request as { method?: unknown } | null | undefined;
      if (!req || typeof req !== "object" || typeof req.method !== "string" || req.method === "") {
        fail("an agent request carries a JSON-RPC request with a method");
      }
      return;
    }
    case "agent.stream":
      if (b.id === "" || b.data === undefined || b.data === null) fail("an agent stream frame has an id and data");
      return;
    case "agent.result":
      if (b.id === "" || b.response === undefined || b.response === null) fail("an agent result has an id and a response");
      return;
    case "control":
      switch (b.action) {
        case "pause":
        case "resume":
        case "stop":
        case "keyboard":
          return;
        case "kick":
          if (!b.user) fail("kick names a user");
          return;
        case "policy":
          if (b.policy === undefined) fail("policy carries the policy");
          return;
      }
      fail(`unknown action ${JSON.stringify(b.action)}`);
      return;
    default:
      return;
  }
}

/** Check and normalise one parsed frame, Go Decode's checks in Go's order. */
export function check(raw: unknown): Envelope {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new ProtocolError(`cannot unmarshal ${typeName(raw)} into an envelope`);
  }
  const obj = raw as Record<string, unknown>;
  // The envelope itself is read leniently (json.Unmarshal ignores unknown
  // keys); only the body is read with DisallowUnknownFields.
  const e = shapeStruct(envelopeSpec as Extract<Spec, { t: "struct" }>, obj, "", false);
  if (e.v !== Version) {
    throw new ProtocolError(
      `unsupported envelope version: ${String(e.v)} (this build speaks ${Version})`,
      "version",
    );
  }
  const type = e.type as string;
  if (!Object.hasOwn(Senders, type)) throw new ProtocolError(`unknown frame type ${JSON.stringify(type)}`);
  const from = e.from as Actor;
  if (!maySend(from.kind, type)) throw new ProtocolError(`a ${from.kind} may not send ${type}`);
  const t = type as FrameType;
  const hasBody = Object.hasOwn(obj, "body");
  if (t === "ping" || t === "pong") {
    const b = obj.body;
    const empty = b === null || (typeof b === "object" && !Array.isArray(b) && Object.keys(b).length === 0);
    if (hasBody && !empty) throw new ProtocolError(`${t} has no body`);
    if (hasBody) e.body = b;
    return e as unknown as Envelope;
  }
  if (!hasBody) throw new ProtocolError(`${t} needs a body`);
  const body = shape(bodySpecs[t], obj.body, `${t} body`, true) as Record<string, unknown>;
  checkBody(t, body);
  e.body = body;
  return e as unknown as Envelope;
}

/** Read one frame (Go `Decode`). Throws a ProtocolError on anything Go refuses. */
export function decode(raw: string): Envelope {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new ProtocolError(err instanceof Error ? err.message : String(err));
  }
  return check(parsed);
}

/**
 * A frame's wire form (Go `Encode`). Unlike Go's, it checks the frame first:
 * the browser never sends what the relay would refuse.
 */
export function encode(e: Envelope): string {
  return JSON.stringify(check(e));
}

/** A frame from this browser, stamped now (Go `New`). */
export function frame<T extends FrameType>(
  type: T,
  from: Actor,
  body: BodyOf[T],
  share?: string,
): EnvelopeFor<T> {
  const ts = goTime(new Date().toISOString(), "ts");
  const e: Record<string, unknown> = { v: Version, type, ts, from };
  if (share) e.share = share;
  if (body !== null) e.body = body;
  return e as unknown as EnvelopeFor<T>;
}

// ---- recordings (Go protocol/recording.go) -------------------------------------

/** What a recording holds (Go `RecordingManifest`). */
export interface RecordingManifest {
  source: Source;
  title: string;
  /** [from, to], inclusive: the session's own turn numbers. */
  turns: [number, number];
  event_count: number;
  masked: Masked[];
  created_at: string;
  sidecar_version: string;
  share?: string;
  screen_bytes?: number;
  /** The class assignment a recording made after the fact is of (RFC-043 model 19). */
  assignment?: { class: string; key: string };
  /** A task score (RFC-037) computed on the owner's machine; never on the relay. */
  score?: Score;
}

/** A task score as RFC-037 gives it (Go `Score`). */
export interface Score {
  total: number;
  band?: string;
  parts?: { name: string; points: number; of: number; reason?: string }[];
  version: string;
}

/**
 * One line of a recording (Go `DecodeEvent`): an event body, checked as an
 * event frame's body is. Throws a ProtocolError on anything Go refuses.
 */
export function decodeEvent(line: string): EventBody {
  let parsed: unknown;
  try {
    parsed = JSON.parse(line);
  } catch (err) {
    throw new ProtocolError(err instanceof Error ? err.message : String(err));
  }
  const body = shape(bodySpecs.event, parsed, "event body", true) as Record<string, unknown>;
  checkBody("event", body);
  return body as unknown as EventBody;
}
